import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import "@/index.css";
import App from "@/App";
import { queryClient } from "@/lib/queryClient";
import { getFbpCookie, getFbcCookie, fireMetaEvents } from "@/utils/metaPixel";
import { attributionHeader } from "@/utils/attribution";
import { rewriteApiUrl } from "@/api/pathRewrite";

// Interim shim: rewrite legacy /api/... paths to the refactored backend's
// /api/v2/... scheme centrally, so existing call sites are untouched until the
// endpoints + zustand refactor lands. See src/api/pathRewrite.js.
const _originalFetch = window.fetch.bind(window);
// Non-GET fetch() calls to our API carry the same Meta/attribution headers as
// axios (several conversion paths -- login, signup, payments -- use fetch).
const withTrackingHeaders = (url, init) => {
  const method = ((init && init.method) || "GET").toUpperCase();
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const ours = typeof url === "string" && (url.startsWith("/api/") || url.startsWith(`${backend}/api/`));
  if (method === "GET" || !ours) return init;
  try {
    const headers = new Headers((init && init.headers) || {});
    const fbp = getFbpCookie();
    const fbc = getFbcCookie();
    if (fbp && !headers.has("X-Meta-Fbp")) headers.set("X-Meta-Fbp", fbp);
    if (fbc && !headers.has("X-Meta-Fbc")) headers.set("X-Meta-Fbc", fbc);
    const attribution = attributionHeader();
    if (attribution && !headers.has("X-GN-Attribution")) headers.set("X-GN-Attribution", attribution);
    return { ...init, headers };
  } catch (e) {
    return init;
  }
};

window.fetch = (input, init) => {
  if (typeof input === "string") {
    const url = rewriteApiUrl(input);
    return _originalFetch(url, withTrackingHeaders(url, init));
  }
  if (input instanceof Request) {
    return _originalFetch(new Request(rewriteApiUrl(input.url), input), init);
  }
  return _originalFetch(input, init);
};

// Configure axios to send auth token from localStorage if available
axios.interceptors.request.use(
  (config) => {
    // Always include credentials for cookies
    config.withCredentials = true;

    // Rewrite legacy API paths to the new backend scheme.
    if (config.url) {
      config.url = rewriteApiUrl(config.url);
    }

    // Also try to add Authorization header from localStorage as fallback
    const token = localStorage.getItem('session_token') || localStorage.getItem('auth_token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add Meta tracking cookies as headers for server-side event deduplication
    const fbp = getFbpCookie();
    const fbc = getFbcCookie();
    if (fbp) config.headers['X-Meta-Fbp'] = fbp;
    if (fbc) config.headers['X-Meta-Fbc'] = fbc;

    // UTM / click attribution for orders, bookings, registrations and signups.
    if ((config.method || 'get').toLowerCase() !== 'get') {
      const attribution = attributionHeader();
      if (attribution) config.headers['X-GN-Attribution'] = attribution;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Server-confirmed Meta events (Purchase, Schedule, WorkshopRegister, ...) come
// back as `meta_events` with the server's event_id; fire their browser copies
// here so every axios call site is covered. fireMetaEvents ignores repeats, so a
// component that also fires them explicitly can't double-count.
axios.interceptors.response.use((response) => {
  if (response && response.data && Array.isArray(response.data.meta_events)) {
    fireMetaEvents(response.data.meta_events);
  }
  return response;
});

// Suppress React error overlay for non-critical concurrent rendering warnings
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === 'string' && 
      (args[0].includes('concurrent rendering') || 
       args[0].includes('synchronously rendering'))
    ) {
      return; // Suppress this specific warning
    }
    originalConsoleError.apply(console, args);
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
    <Toaster richColors closeButton position="top-center" />
  </QueryClientProvider>
);
